import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Grid, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IconSearch, IconFileExport } from '@tabler/icons-react';
import { Button, DateTimePicker, IconButton } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { usePermissions, useURLparams } from '@hooks';
import { getDatesFromTheLastMonth, isoStringToDate, toISOStringLocal } from '@utils';
import { zodResolver } from '@hookform/resolvers/zod';
import searchBoxSchema from './SearchBox.schema';
import useDownloadConsolidatedDispersionsList from '../data/useDownloadConsolidatedDispersionsList';
import styles from '../styles';

const DATE_PICKER_PROPS = {
  size: 'medium',
  dateOnly: true,
  readOnly: false,
  disablePast: false,
  calendarViews: ['year', 'month', 'day'],
  fullWidth: true,
};

interface props {
  filtersOpen?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  isLoading?: boolean;
  dataLength?: number;
}

/**
 * Displays filters
 * @param {props} props
 * @param {object} props.params
 */
const SearchBox: React.FC<props> = ({ isLoading = false, dataLength }: props) => {
  const { params, setURLparams } = useURLparams<{
    toDate?: string;
    fromDate?: string;
    customerId?: string;
  }>();
  const { t } = useTranslation();
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { fromDate, toDate } = getDatesFromTheLastMonth();
  const { download, isLoading: isLoadingDownload } = useDownloadConsolidatedDispersionsList();
  const isAllowedToDownloadDispersionReport = usePermissions([
    PERMISSIONS.downloadDispersionReport,
  ]);
  /**
   * @function search
   * @description This function is used to search the list.
   */
  const onSubmit = (_params: { toDate?: Date; fromDate?: Date }) => {
    const newParams: Record<string, string> = {
      ...(params?.customerId && { customerId: params.customerId }),
      ...(_params?.fromDate && { fromDate: toISOStringLocal(_params.fromDate) }),
      ...(_params?.toDate && { toDate: toISOStringLocal(_params.toDate) }),
    };
    setURLparams(newParams);
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      fromDate: params.fromDate ? isoStringToDate(params.fromDate) : fromDate,
      toDate: params.toDate ? isoStringToDate(params.toDate) : toDate,
    },
    resolver: zodResolver(searchBoxSchema),
    mode: 'onChange',
  });
  const disableButton = isLoading || isSubmitting;

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('DISPERSIONS:FILTERS_TITLE')}</Typography>
      </Box>
      <Box sx={styles.content(responsive)}>
        <Box sx={styles.firstLine(responsive)}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Controller
                name='fromDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.fromDate)}
                    inputLabel='COMMON:FROM_DATE'
                    helperText={errors.fromDate ? String(errors.fromDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Controller
                name='toDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.toDate)}
                    inputLabel='COMMON:TO_DATE'
                    helperText={errors.toDate ? String(errors.toDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={styles.actionsContainer(responsive)}>
          <Button
            customStyle={styles.clearButton}
            text={t('COMMON:CLEAR_FILTER')}
            color='primary'
            variant='link'
            onClick={() => {
              reset({ fromDate, toDate });
              onSubmit({ fromDate, toDate });
            }}
          />
          {isAllowedToDownloadDispersionReport && (
            <IconButton
              aria-label={t('CONSUMPTION:DOWNLOAD')}
              tooltip='CONSUMPTION:DOWNLOAD'
              onClick={() => {
                download(params);
              }}
              IconComponent={IconFileExport}
              isLoading={isLoadingDownload}
              disabled={!dataLength}
            />
          )}
          <Button
            customStyle={styles.searchButton}
            startIcon={
              <SvgIcon>
                <IconSearch />
              </SvgIcon>
            }
            variant='contained'
            color='primary'
            type='button'
            disabled={disableButton}
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBox;
